import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate, useParams, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Nav from "./components/Nav.jsx";
import Footer from "./components/Footer.jsx";
import Home from "./pages/Home.jsx";
import HomeBold from "./pages/HomeBold.jsx";
import How from "./pages/How.jsx";
import Results from "./pages/Results.jsx";
import About from "./pages/About.jsx";
import Contact from "./pages/Contact.jsx";
import Pricing from "./pages/Pricing.jsx";
import PricingV2 from "./pages/PricingV2.jsx";
import Privacy from "./pages/Privacy.jsx";
import TermsOfSale from "./pages/TermsOfSale.jsx";
import TermsOfUse from "./pages/TermsOfUse.jsx";
import ReturnPolicy from "./pages/ReturnPolicy.jsx";
import CookiePolicy from "./pages/CookiePolicy.jsx";
import AccountDelete from "./pages/AccountDelete.jsx";
import AccountDeleteConfirm from "./pages/AccountDeleteConfirm.jsx";
import AccountDeleteDone from "./pages/AccountDeleteDone.jsx";
import GoogleBusinessProfile from "./pages/GoogleBusinessProfile.jsx";
import CookieBanner from "./components/CookieBanner.jsx";

import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "./i18n/index.js";
import { maybeRedirectToGeoLocale } from "./lib/geo.js";

const TWEAK_DEFAULTS = {
  heroWash: "wash",
  headlineWeight: "bold",
  proofColor: "ink",
  homeVariant: "bold"
};

function LocaleLayout() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED_LOCALES.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  if (lang && !SUPPORTED_LOCALES.includes(lang)) {
    return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  }

  const route = currentRouteId(pathname);
  const overDark = route === "home" && TWEAK_DEFAULTS.homeVariant === "bold";

  return (
    <>
      <Nav overDark={overDark} />
      <Outlet context={{ tweaks: TWEAK_DEFAULTS }} />
      <Footer />
      <CookieBanner />
    </>
  );
}

function currentRouteId(pathname) {
  const seg = pathname.split("/").filter(Boolean);
  return seg[1] || "home";
}

function GeoRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [decided, setDecided] = React.useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await maybeRedirectToGeoLocale(navigate, location);
      if (!cancelled) setDecided(true);
    })();
    return () => { cancelled = true; };
  }, [navigate, location]);

  // Once the geo lookup resolved without redirecting (or the user is on a
  // locale we don't have a country mapping for), fall back to the default.
  if (decided) return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  return null;
}

function HomeWrapper() {
  return TWEAK_DEFAULTS.homeVariant === "bold"
    ? <HomeBold tweaks={TWEAK_DEFAULTS} />
    : <Home tweaks={TWEAK_DEFAULTS} />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<GeoRedirect />} />
      <Route path="/:lang" element={<LocaleLayout />}>
        <Route index element={<HomeWrapper />} />
        <Route path="how" element={<How />} />
        <Route path="results" element={<Results />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="pricing-v2" element={<PricingV2 />} />
        <Route path="fair-hospitality" element={<About />} />
        <Route path="about" element={<Navigate to="../fair-hospitality" replace />} />
        <Route path="contact" element={<Contact />} />
        <Route path="integrations/google-business-profile" element={<GoogleBusinessProfile />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms-of-sale" element={<TermsOfSale />} />
        <Route path="terms" element={<TermsOfUse />} />
        <Route path="return-policy" element={<ReturnPolicy />} />
        <Route path="cookie-policy" element={<CookiePolicy />} />
        <Route path="account/delete" element={<AccountDelete />} />
        <Route path="account/delete/confirm" element={<AccountDeleteConfirm />} />
        <Route path="account/delete/done" element={<AccountDeleteDone />} />
      </Route>
      <Route path="/privacy"        element={<Navigate to={`/${DEFAULT_LOCALE}/privacy`} replace />} />
      <Route path="/terms-of-sale"  element={<Navigate to={`/${DEFAULT_LOCALE}/terms-of-sale`} replace />} />
      <Route path="/terms"          element={<Navigate to={`/${DEFAULT_LOCALE}/terms`} replace />} />
      <Route path="/terms-of-use"   element={<Navigate to={`/${DEFAULT_LOCALE}/terms`} replace />} />
      <Route path="/return-policy"  element={<Navigate to={`/${DEFAULT_LOCALE}/return-policy`} replace />} />
      <Route path="/cookie-policy"  element={<Navigate to={`/${DEFAULT_LOCALE}/cookie-policy`} replace />} />
      {/* Locale-less URL submitted in Google's OAuth verification form. */}
      <Route path="/integrations/google-business-profile" element={<Navigate to={`/${DEFAULT_LOCALE}/integrations/google-business-profile`} replace />} />
      {/* Public account-deletion landing URL — submitted to Google Play's Data
          Safety form. Locale-less; redirects to the default locale, preserving
          the magic-link ?token= on the confirm path. */}
      <Route path="/account/delete"          element={<LocalelessRedirect to="account/delete" />} />
      <Route path="/account/delete/confirm"  element={<LocalelessRedirect to="account/delete/confirm" />} />
      <Route path="/account/delete/done"     element={<LocalelessRedirect to="account/delete/done" />} />
      <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
    </Routes>
  );
}

/**
 * Top-level redirect that preserves the query string (notably `?token=...`
 * on the confirm path) when bouncing to the default-locale URL. The plain
 * Navigate components above strip search params silently.
 */
function LocalelessRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`/${DEFAULT_LOCALE}/${to}${search ?? ""}`} replace />;
}
